import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Autocomplete, Badge, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, FormGroup, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import classNames from 'classnames';
import { differenceInCalendarDays, format } from 'date-fns';
import { nanoid } from 'nanoid';
import numeral from 'numeral';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiResponse } from '../api/axios';
import { Shape, ShapeType } from '../core/global';
import { CountryType, allCountries } from '../data/countries';
import styles from "../styles/components/form-components.module.scss";
import tableStyles from "../styles/components/table.module.scss";
import { ErrorMessage, SuccessMessage } from './error-message';
import { InfoModal } from './info-modal';
import { JsonField } from './json-field';
import { ModalTitle } from './modal-title';
import { Textarea } from './mui-styled-textarea';
import { StyledMarkdown } from './typeface';
import { Uploader } from './uploader';


export interface FormObject {
  [key: string]: any
}

export interface CreateFormProps {
  shape: Shape[]
  onChange?: (data: FormObject, key: string) => void
  onSubmit?: (data: FormObject) => Promise<ApiResponse>
  onBeforeSubmit?: () => boolean
  defaultValues: FormObject | null
  onCancel?: () => void
  isModal?: boolean
  title?: React.ReactNode
  intro?: React.ReactNode
  inline?: boolean
  successMessage?: string
  onSuccess?: (response: any) => void
  onError?: (message?: string | null | undefined) => void
  submitButtonLabel?: string
  oneButtonSubmit?: boolean
  allowEdit?: boolean
  children?: React.ReactNode
  open?: boolean
  onDelete?: (data: FormObject) => Promise<ApiResponse>
  size?: 'small' | 'medium'
  disableSubmit?: boolean
  showErrors?: boolean
  className?: string
  defaltIsEdited?: boolean
}

interface GetValue {
  values: FormObject | null
  key: string
  type?: ShapeType | undefined
  defaultValue?: any
}

const getValue = ({
  values,
  key,
  type,
  defaultValue = ''
}: GetValue
) => {
  if (values && Object.prototype.hasOwnProperty.call(values, key)) {
    let val = values[key]
    if (type && type === 'checkbox') {
      // Oh well...
      val =  val === true ? 'Yes' : 'No'
    }
    return val
  }
  return defaultValue
}

interface CountrySelectProps {
  onChange: (event: CountryType | null) => void
  value?: string
}

export function CountrySelect({
  onChange,
  value,
}: CountrySelectProps) {
  return (
    <Autocomplete
      id="country"
      sx={{ mb: '16px' }}
      options={allCountries}
      autoHighlight
      getOptionLabel={(option: CountryType) => option.label}
      autoComplete
      onChange={(event: any, newValue: CountryType | null) => {
        onChange(newValue);
      }}
      inputValue={value}
      renderOption={(props, option) => {
        const {...optionProps } = props;
        return (
          <Box
            component="li"
            // sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
            {...optionProps}
            key={nanoid()}
          >
            {option.label} ({option.code}) +{option.phone}
          </Box>
        );
      }}
      renderInput={(params) => {
        return (
          <TextField 
            {...params}
            label="Choose a country"
          />
        )
      }}
    />
  );
}

interface FormWrapperProps {
  isModal: boolean
  children: React.ReactNode
  submitButtons?: React.ReactNode
  title?: React.ReactNode
  intro?: React.ReactNode
  onSubmit?: (data: any) => void
  open?: boolean
  onClose?: () => void
  className?: string
}

export const FormWrapper = ({
  isModal,
  children,
  submitButtons,
  title,
  intro,
  onSubmit,
  open = false,
  onClose,
  className,
}: FormWrapperProps) => {
  const _onSubmit = useCallback((event: any) => {
    if (onSubmit) {
      event.stopPropagation();  
      event.preventDefault()
      onSubmit(event)
    }
  }, [onSubmit])

  if (isModal) {
    return (
      <>
        <Dialog
          className={classNames(className)}
          open={open}
          onClose={onClose}
          fullWidth
          // PaperProps={{
          //   component: 'form',
          //   onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
          //     _onSubmit(event)
          //   },
          // }}
        >
          <ModalTitle onClose={onClose}>{title}</ModalTitle>
          <form 
            className={classNames(styles.form, className)}
            onSubmit={_onSubmit}
          >
            <DialogContent
              sx={{ display: 'flex', flexDirection: 'column', width: '100%', paddingTop: intro ? '6px !important' : '0' }}
            >
              {intro && typeof intro === 'string' ? 
                <StyledMarkdown>{intro}</StyledMarkdown>
                :
                intro
              }
              {children}
            </DialogContent>
            {submitButtons}
          </form>
        </Dialog>
      </>
    )
  }
  return (
    <>
      <form 
        className={classNames(styles.form, className)}
        onSubmit={_onSubmit}
      >
        {children}
        {submitButtons}
      </form>
    </>
  )
}

export const CreateForm = ({
  shape,
  onChange,
  defaultValues,
  onSubmit,
  onCancel,
  isModal = false,
  title = '',
  intro = '',
  inline = false,
  successMessage = 'Your changes have been saved successfully',
  onSuccess,
  onError,
  submitButtonLabel = 'Save changes',
  oneButtonSubmit = false,
  allowEdit = true,
  children,
  onDelete,
  open: _open = true,
  size = 'medium',
  disableSubmit = false,
  showErrors = true,
  onBeforeSubmit,
  className,
  defaltIsEdited = false,
}: CreateFormProps) => {
  const [showPassword, setShowPassword] = useState(false)
  const [success, setSuccess] = useState<string[]>([])
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false)
  const [values, setValues] = useState<FormObject>({})
  const [isEdited, setIsEdited] = useState(defaltIsEdited)
  const [open, setOpen] = useState(_open)
  const [toDelete, setToDelete] = useState(false)
  const [datePickerOpen, setDatePickerOpen] = React.useState(false);
  // Keyed by shape key. A json field reports itself invalid while its text
  // doesn't parse, and Save stays disabled until every entry is valid.
  const [invalidFields, setInvalidFields] = useState<FormObject>({})

  const setFieldValidity = useCallback((key: string, valid: boolean) => {
    setInvalidFields( current => {
      const isInvalid = !valid
      if (Boolean(current[key]) === isInvalid) {
        return current
      }
      return {...current, [key]: isInvalid}
    })
  }, [])

  const hasInvalidFields = useMemo(() => {
    return Object.values(invalidFields).some(Boolean)
  }, [invalidFields])
  
  const onClose = useCallback(() => {
    setOpen(false)
    if (onCancel) {
      onCancel() 
    }
  }, [onCancel])


  useEffect(() => {
    setErrors([])
    setSuccess([])
  }, [])

  useEffect(() => {
    const initialValues: FormObject = {};
    let hasShapeDefaults = false;
  
    shape.forEach(field => {
      if (
        field.defaultValue !== undefined &&
        field.defaultValue !== null
      ) {
        initialValues[field.key] = field.defaultValue;
        hasShapeDefaults = true;
      }
    });
  
    const nextValues = {
      ...initialValues,
      ...(defaultValues ?? {}),
    };
  
    setValues(nextValues);
  
    if (hasShapeDefaults && onChange) {
      Object.keys(initialValues).forEach(key => {
        if (
          !defaultValues ||
          !Object.prototype.hasOwnProperty.call(defaultValues, key)
        ) {
          onChange(nextValues, key);
        }
      });
    }
  }, [shape, defaultValues]);

  const updateValue = useCallback((value: string | boolean | null | File[], key: string): FormObject => {
    setIsEdited(true)
    const useValues = {
      ...values,
      [key]: value
    }
    setValues(useValues)
    return useValues
  }, [values])

  const boxStyle = useMemo(() => {
    return { 
      display: 'flex', 
      flexWrap: 'wrap', 
      width: '100%',
      maxWidth: '100%',
      flexDirection: `${inline ? `row` : `column`}` 
    }
  }, [inline])

  return (
    <>
      <FormWrapper 
        open={open}
        onClose={onClose}
        className={classNames(className, {
          [styles.inline]: inline,
          [styles.isLoading]: loading
        }, styles.formWrapper)}
        onSubmit={async (event: React.SyntheticEvent) => {
            // const err = true
            if (onBeforeSubmit && onBeforeSubmit() === false) {
              return
            }
            // Belt and braces: the button is disabled too, but a form can also
            // be submitted with Enter.
            if (hasInvalidFields) {
              return
            }
            setLoading(true)
            setSuccess([])
            // if (onSubmit && !err) {
            if (onSubmit) {
              const {response, error, message} = await onSubmit(values)
              if (error !== 200) {
                setErrors( e => [`${message}`])
                if (onError) {
                  onError(message)
                }
              } else {
                setErrors([])
                setIsEdited(false)
                setSuccess([successMessage])
                if (onSuccess) {
                  onSuccess(response)
                }
              }
            }          
            setLoading(false)
          }
        } 
        title={title} 
        intro={intro} 
        isModal={isModal}
        submitButtons={
          <>
          {!allowEdit ? null :
            <>{oneButtonSubmit ? 
              <>
                <DialogActions sx={isModal ? { pb: 3, px: 3 } : {pb: 0, px: 0, pt: 0}}>
                  <Button disabled={disableSubmit || hasInvalidFields} variant="contained" type="submit">
                    {submitButtonLabel}
                  </Button>
                </DialogActions>
              </>
            : 
              <>
                {isEdited ? 
                  <>
                    <DialogActions sx={isModal ? { pb: 3, px: 3 } : {pb: 0, px: 0, pt: 0}}>
                      <Button onClick={onClose}>Cancel</Button>
                      <Button disabled={disableSubmit || hasInvalidFields} variant="contained" type="submit">
                        {submitButtonLabel}
                      </Button>
                    </DialogActions>
                  </>
                  :
                  <>
                    <DialogActions sx={isModal ? { pb: 3, px: 3 } : {pb: 0, px: 0, pt: 0}}>
                      <>
                      {onDelete && 
                        <>
                          {!toDelete ? 
                          <Button 
                            variant='contained' 
                            color="warning"
                            disabled={disableSubmit}
                            onClick={() => {
                              setToDelete(true)
                            }
                          }>Delete</Button>
                          :
                          <Button 
                            variant='contained' 
                            color="error"
                            disabled={disableSubmit}
                            onClick={() => {
                              const execute = async () => {
                                setLoading(true)
                                setSuccess([])
                                if (onDelete) {
                                  const {response, error, message} = await onDelete(values)
                                  if (error !== 200) {
                                    setErrors( e => [`${message}`])
                                    if (onError) {
                                      onError(message)
                                    }
                                  } else {
                                    if (onSuccess) {
                                      onSuccess(response)
                                    }
                                    onClose()
                                  }
                                }
                                setLoading(false)
                              }
                              execute()

                            }
                          }>Confirm delete</Button>
                        }
                        </>
                      }
                      </>
                      <Button onClick={onClose}>
                        {isModal ? `Close` : `Cancel` }
                      </Button>
                    </DialogActions>
                  </>
                }
              </>
            }</>
          }
          </>
        }
      >
        {/* {showMessage && <SuccessMessage message={'Your account has been updated.'} />}
        */}
        <Box sx={boxStyle}>
            {!inline && <>
              {showErrors && <ErrorMessage message={errors} />}
              <SuccessMessage message={success} />
            </>
            }
            <FormGroup 
              sx={boxStyle}
            >
              {shape.map( (item: Shape) => {
                const {
                  config, 
                  placeholder, 
                  key, 
                  required, 
                  type, 
                  note, 
                  noteTitle,
                  showNoteModal,
                  showWhenTrue, 
                  editable,
                  defaultValue
                } = item

                let {
                  title, 
                  options, 
                } = item

                const value = getValue({values, key, defaultValue})
                const onChangeValue = (val: string | boolean | File[], valueKey: string | undefined | null = undefined) => {
                  const useKey = valueKey || key
                  const result = updateValue(val, useKey)
                  if (onChange) {
                    onChange(result, useKey)
                  }
                }
                // Only show elements that meet all criteria
                if (showWhenTrue && Array.isArray(showWhenTrue) && showWhenTrue.length > 0) {
                  const valid = showWhenTrue.filter( (itemKey: string) => {
                    const value = getValue({values, key: itemKey, defaultValue})
                    return (value === true || value === 'true')
                  })
                  if (valid.length !== showWhenTrue.length) {
                    return null
                  }
                }
                
                if (type && type === 'repeatable-group' && config) {
                  const min = config.min || 0
                  const max = config.max || 10
                  title = config.selectorLabel || title
                  const itemLabel = config.itemLabel || 'Item'
                  options = []
                  for (let i = min; i <= max; i++) {
                    options.push({
                      key: i,
                      value: `${itemLabel} ${i}`
                    })
                  }
                }

                const additionalFields: Shape[] = []

                if (type 
                  && type === 'repeatable-group' 
                  && config && config.fields 
                  && Array.isArray(config.fields) 
                  && config.fields.length > 0) {
                    if (values && Object.prototype.hasOwnProperty.call(values, key)) {
                      const numberOfGroups = Number(values[key]) || 0
                      if (typeof numberOfGroups === 'number' && numberOfGroups > 0) {
                        const min = config.min || 0
                        for (let i = min; i <= numberOfGroups; i++) {
                          const fields = config.fields.map( (field: Shape) => {
                            return {
                              ...field,
                              title: `${field.title} ${i}`,
                              key: `${key}-${i}`
                            }
                          })
                          additionalFields.push(...fields)
                        }
                      }
                    }
                }

                if (options && Array.isArray(options) && options.length > 0) {
                  const isMultiselect = !!(type && type === 'multi-select')
                  let  useSelectValue = value
                  if (isMultiselect) {
                    useSelectValue = (Array.isArray(value) ? value : [(value || '')]).filter(v => v !== '').map(v => String(v)) as string[]
                    // must be unique values
                    useSelectValue = Array.from(new Set(useSelectValue))
                  }
                  return (
                    <React.Fragment key={key}>
                      <FormControl className={styles.overflowBox}>
                        <InputLabel id={key}>{title}</InputLabel>
                        <Select
                          className={styles.select}
                          label={title}
                          labelId={key}
                          id={key}
                          multiple={isMultiselect}
                          required={required || false}
                          value={useSelectValue}
                          onChange={(event: SelectChangeEvent) => {
                              onChangeValue(event.target.value as string)
                            }
                          }
                        >
                          {options.map(({key, value}) => {
                            return <MenuItem key={`${key}`} value={`${key}`}>{value}</MenuItem>
                          })}
                        </Select>
                      </FormControl>
                      <NoteField note={note} noteTitle={noteTitle} showNoteModal={showNoteModal} />
                      {additionalFields && additionalFields.length > 0 && additionalFields.map( (field: Shape) => {
                        const inputValue = getValue({values, key: field.key, type: field.type, defaultValue})

                        return <React.Fragment key={field.key}>
                          <CreateFormInput 
                            title={field.title} 
                            placeholder={field.placeholder}
                            inputType={field.type || 'text'}
                            inputValue={inputValue}
                            onChangeValue={(val: string) => {
                              onChangeValue(val, field.key)
                            }}
                            required={field.required}
                            note={field.note}
                            />
                          </React.Fragment>
                        })
                      }
                    </React.Fragment>
                    
                  )
                }
                if (type && type === 'file') {
                  const message = 'Error uploading your file(s), please try again...'
                  return  <>
                    <Uploader 
                      small
                      config={config}
                      placeholder={placeholder || title}
                      onUploadCompleted={(content, type, file) => {}}
                      onError={(err: Error) => {
                        setErrors( e => [`${message}`])
                        if (onError) {
                          onError(message)
                        }
                      }}
                      onDrop={(acceptedFiles: File[]) => {
                        if (acceptedFiles) {
                          onChangeValue(acceptedFiles)
                        }
                      }}
                    />
                  </>
                }
                if (type && type === 'checkbox') {
                  return (
                    <div key={key} className={styles.checkboxWrapper}>
                      <FormControlLabel
                        className={styles.checkbox}
                        control={
                          <Checkbox
                            readOnly={editable === false}
                            value="remember" 
                            checked={value === true || value === 'true'}
                            onChange={(e: React.SyntheticEvent) => {
                              const useValue = (value === true || value === 'true')
                              if (editable !== false) onChangeValue(!useValue)
                            }}
                            color="primary"
                          />}
                        label={
                          <>{editable === false ? 
                            <Badge
                              className={styles.badge}
                              badgeContent={'not editable'}
                              color="primary" 
                              
                            >
                              {title}
                            </Badge>
                            :
                            title
                          }</>
                        }
                      />
                      <NoteField note={note} noteTitle={noteTitle} showNoteModal={showNoteModal} />
                    </div>
                  )
                }
                if (type && type === 'textarea') {
                  return (<Textarea
                    key={key}
                    title={title}
                    required={required}
                    placeholder={placeholder || title}
                    value={value || ''}
                    onChange={(val: string) => {
                      onChangeValue(val)
                    }}
                  />)
                }

                if (type && type === 'json') {
                  return (
                    <JsonField
                      key={key}
                      value={value}
                      title={title}
                      note={note}
                      placeholder={placeholder}
                      required={required}
                      editable={allowEdit && editable !== false}
                      rows={config?.rows || 10}
                      onChange={(val: string) => {
                        onChangeValue(val)
                      }}
                      onValidityChange={(valid: boolean) => {
                        setFieldValidity(key, valid)
                      }}
                    />
                  )
                }

                let inputType = 'text'
                const inputValue = value === null || typeof value === "undefined" ? '' : value
                if (type && type === 'password' && !showPassword) {
                  inputType = 'password'
                } else if (type && type === 'date') {
                  const dateValue = value ? new Date(value) : null
                  const isValidDate = dateValue !== null && !Number.isNaN(dateValue.getTime())
                  const days = isValidDate ? differenceInCalendarDays(dateValue, new Date()) : null
                  return (
                    <div key={key} className={styles.dateRange}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label={title}
                          // Without this the picker renders empty even when the
                          // record has a date, and the chip below disagrees with it.
                          value={isValidDate ? dateValue : null}
                          disabled={editable === false}
                          onChange={(newValue: string | Date | null) => {
                            if (!newValue) {
                              onChangeValue('')
                              return
                            }
                            const parsed = new Date(newValue)
                            if (!Number.isNaN(parsed.getTime())) {
                              onChangeValue(format(parsed, 'yyyy-MM-dd'))
                            }
                          }}
                        />
                      </LocalizationProvider>
                      {days !== null &&
                        <Chip onClick={() => {}} label={`${numeral(days).format("0,0")} days`} color={days < 0 ? "error" : "default" } variant="outlined" />
                      }
                    </div>
                  );
                }
                if (type && type === 'password') {
                  return (
                    <FormControl key={key}>
                        <span className={classNames(styles.inputWrapper, styles.inlineButton)}>
                          <TextField
                            size={size}
                            type={inputType}
                            sx={{ mb: `${inline ? `0` : `16px` }`, width: `${inline ? `auto` : `100%` }` }}
                            id={key}
                            name={key}
                            label={title}
                            placeholder={placeholder || title}
                            value={inputValue}
                            required={required || false}
                            fullWidth
                            variant="outlined"
                            onClick={(e: React.SyntheticEvent) => {
                              e.preventDefault()
                              e.stopPropagation()
                            }}
                            onChange={(event: React.SyntheticEvent) => {
                              // @ts-ignore
                              const val = event.currentTarget.value as string
                              onChangeValue(val)
                            }}
                          />
                            <IconButton
                              className={styles.passwordToggle}
                              aria-label="toggle password visibility"
                              onClick={()=> {
                                setShowPassword(!showPassword)
                              }}
                            >
                              {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                            </IconButton>
                        </span>
                        <NoteField note={note} noteTitle={noteTitle} showNoteModal={showNoteModal} />
                    </FormControl>
                  )
                }
                return (
                  <React.Fragment key={key}>
                    <CreateFormInput 
                      id={key}
                      title={title} 
                      placeholder={placeholder}
                      inputType={inputType}
                      inputValue={inputValue}
                      onChangeValue={(val: string) => {
                        onChangeValue(val)
                      }}
                      required={required}
                      note={note}
                      inline={inline}
                      size={size}
                      noteTitle={noteTitle} 
                      showNoteModal={showNoteModal}
                    />
                  </React.Fragment>
                )
              })}
            </FormGroup>
        </Box>
        {children}
        {loading && <div className={styles.loader}><CircularProgress /></div>}
      </FormWrapper>
      {inline && <>
          {showErrors && <ErrorMessage message={errors} />}
          <SuccessMessage message={success} />
        </>
      }
    </>
  );
}

export const CreateFormInput = ({
  title = '',
  placeholder = '',
  inputType = 'text',
  inputValue = '',
  onChangeValue = (val: string) => {},
  required = false,
  note,
  inline = false,
  size = 'medium',
  id = nanoid(),
  noteTitle,
  showNoteModal,
}: {
  title: string
  placeholder?: string
  inputType?: string
  inputValue?: string
  onChangeValue?: (val: string) => void
  required?: boolean
  note?: string
  inline?: boolean
  size?: 'small' | 'medium'
  id?: string
  noteTitle?: string
  showNoteModal?: boolean
}) => {
  return (
    <FormControl>
        <span className={classNames(styles.inputWrapper, {
            [styles.inlineButton]: showNoteModal,
        })}>
          <TextField
            size={size}
            type={inputType}
            sx={{ mb: `${inline ? `0` : `16px` }`, width: `${inline ? `auto` : `100%` }` }}
            id={id}
            name={id}
            label={title}
            placeholder={placeholder || title}
            value={inputValue}
            required={required || false}
            fullWidth
            variant="outlined"
            onClick={(e: React.SyntheticEvent) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onChange={(event: React.SyntheticEvent) => {
              // @ts-ignore
              const val = event.currentTarget.value as string
              onChangeValue(val)
            }}
          />
          <NoteField note={note} noteTitle={noteTitle} showNoteModal={showNoteModal} />
        </span>
    </FormControl>
  )
}

export const NoteField = ({
  note,
  noteTitle,
  showNoteModal,
}: {
  note?: string
  noteTitle?: string
  showNoteModal?: boolean
}) => {
  const [show, setShow] = useState(false)

  if (!note) {
    return null
  }

  if (showNoteModal) {
    return (
      <>
        <Button
          size='medium'
          color="primary"
          variant="contained"
          onClick={() => {
            setShow(true)
          }}
        >
          ?
          {/* <QuestionMarkIcon /> */}
        </Button>
        <InfoModal
          open={show}
          title={noteTitle || ''}
          content={note}
          maxWidth="sm"
          onClose={() => {
            setShow(false)
          }}
        />
      </>
    )
  }
  return (<StyledMarkdown className={styles.note}>{note}</StyledMarkdown>)
}

interface CreateSummaryProps {
  shape: Shape[]
  values: FormObject | FormObject[] | null
  compact?: boolean
  className?: string
}

export const CreateSummary = ({
  values,
  shape,
  compact = false,
  className,
}: CreateSummaryProps) => {
  const useValues = useMemo(() => {
    if (!Array.isArray(values)) {
      return [values]
    }
    return values
  }, [values])
  
  return (
    <>
      {useValues?.map((itemValues: FormObject | null, index: number) => {

        if (!itemValues) {
          return null
        }

        return (<table className={classNames(tableStyles.table, className, {
          [tableStyles.compact]: compact
        })} key={index}>
          <tbody>
          {shape.map( (item: Shape) => {
            const {title, placeholder, key, required, type, options, note, defaultValue, showWhenTrue} = item
            const value = getValue({values: itemValues, key, type, defaultValue})

            // Only show elements that meet all criteria
            if (showWhenTrue && Array.isArray(showWhenTrue) && showWhenTrue.length > 0) {
              const valid = showWhenTrue.filter( (itemKey: string) => {
                const value = getValue({values: itemValues, key: itemKey, defaultValue})
                return (value === true || value === 'true')
              })
              if (valid.length !== showWhenTrue.length) {
                return null
              }
            }

            const isEmpty = value !== 0 && !value 
              return (
                <tr key={key}>
                  <th className={tableStyles.fieldName}>
                    {title}
                  </th>
                  <td className={classNames(tableStyles.fieldValue, {
                    [tableStyles.isEmpty]: isEmpty
                  })}>
                    {isEmpty ? 'N/A' : value}
                  </td>
                </tr>
              )
            })
          }
          </tbody>
        </table>)
      })
      }
    </>
  )
}